/**
 * Passive collector of timing statistics for `/uia/*` API requests.
 *
 * Uses a `PerformanceObserver` on `resource` entries (POST fetches to
 * `uia/RequestInteraction` and `uia/ProcessAction`) to build a rolling window of
 * request durations, and augments it with the Network Information API
 * (`navigator.connection`) as an extra "connection quality" signal.
 *
 * Resource entries are emitted only once a request has finished, so they cannot answer "is a request
 * hanging right now". That part comes from explicit marks the network layer puts around the actual
 * `fetch` (`markRequestStart` / `markRequestEnd`, see `foundation/request/requestHelper.ts`), exposed
 * as `inFlight` in the snapshot.
 *
 * The collector is a singleton exposing a `subscribe`/`getSnapshot` pair so it can
 * back a React `useSyncExternalStore` hook (see `@libs/hooks` `useUiaRequestStats`).
 * `packages/*` consume the aggregated data through that hook, never this class directly.
 */

/** A single observed `/uia/*` request. */
export interface UiaSample {
    /** Request duration in ms (`PerformanceResourceTiming.duration`). */
    duration: number;
    /** Start time in ms relative to the time origin (`startTime`). */
    startTime: number;
    /** Full request URL. */
    route: string;
}

/** Aggregated timing metrics over the current rolling window. */
export interface UiaTimingStats {
    /** Number of requests in the window. */
    count: number;
    /** Average (mean) duration in ms. */
    average: number;
    /** Median duration in ms. */
    median: number;
    /** Fastest request in ms. */
    min: number;
    /** Slowest request in ms. */
    max: number;
    /** 95th percentile duration in ms (tail latency; more stable than `max`). */
    p95: number;
    /** Duration of the most recent request in ms. */
    last: number;
}

/** Snapshot of the Network Information API, when available. */
export interface UiaConnectionInfo {
    /** Whether `navigator.connection` is available in this browser. */
    supported: boolean;
    /** Effective connection type: `'slow-2g' | '2g' | '3g' | '4g'`. */
    effectiveType?: string;
    /** Estimated downlink bandwidth in Mbit/s. */
    downlink?: number;
    /** Estimated round-trip time in ms. */
    rtt?: number;
    /** Whether the user requested reduced data usage. */
    saveData?: boolean;
}

/**
 * The requests that are on the wire at this very moment.
 *
 * Resource timings only exist once a request has finished, so this part of the snapshot is fed by
 * explicit marks from the network layer (see {@link UiaPerfCollector.markRequestStart}). It is the
 * only signal that can tell "a request is hanging right now" — the Redux status cannot: it flips to
 * Loading at dispatch, i.e. before `action.delay` has even elapsed, and stays there when a thunk is
 * rejected without a payload (an aborted request).
 */
export interface UiaInFlightInfo {
    /** How many `/uia/*` requests are running right now. */
    count: number;
    /** Age of the oldest running request in ms; 0 when nothing is running. */
    oldestElapsed: number;
    /** Whether the oldest running request has already been on the wire longer than the slow threshold. */
    isSlow: boolean;
}

/** Combined snapshot returned to consumers. */
export interface UiaRequestStats extends UiaTimingStats {
    connection: UiaConnectionInfo;
    inFlight: UiaInFlightInfo;
    /**
     * Derived flag: the connection is considered slow when the request tail
     * latency (`p95`) crosses the configured threshold, the reported
     * `effectiveType` is in the configured slow set, or the user opted into
     * data-saving mode. Thresholds are configured via env
     * (`NX_PUBLIC_SLOW_CONNECTION_*`).
     */
    isSlowConnection: boolean;
}

// Minimal typing for the Network Information API, which is not part of the DOM lib.
interface NetworkInformationLike extends EventTarget {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
}

const UIA_MARKER = '/uia/';
const WS_MARKER = '/uia/socket';
const WINDOW_CAPACITY = 50;

const EMPTY_TIMING: UiaTimingStats = { count: 0, average: 0, median: 0, min: 0, max: 0, p95: 0, last: 0 };
const EMPTY_IN_FLIGHT: UiaInFlightInfo = { count: 0, oldestElapsed: 0, isSlow: false };

/** Monotonic clock, immune to wall-clock jumps; falls back to `Date.now` where `performance` is absent. */
const nowMs = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

// --- "Slow connection" thresholds, configurable via env (referenced by full name so
// webpack's DefinePlugin can statically inline them; see `.env`). ---

export const toPositiveNumber = (raw: string | undefined, fallback: number): number => {
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : fallback;
};

/**
 * Latency (ms) at or above which a request is treated as slow — applied both to the `p95` of finished
 * requests and to the age of a request that is still on the wire. Exported for testing.
 */
export const SLOW_P95_MS = toPositiveNumber(process.env.NX_PUBLIC_SLOW_CONNECTION_P95_MS, 3000);

/** `navigator.connection.effectiveType` values treated as slow. */
const SLOW_EFFECTIVE_TYPES = (process.env.NX_PUBLIC_SLOW_CONNECTION_EFFECTIVE_TYPES ?? 'slow-2g,2g')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

/** Pure derivation of the slow-connection flag from timing + connection signals. Exported for testing. */
export const computeIsSlowConnection = (timing: UiaTimingStats, connection: UiaConnectionInfo): boolean => {
    if (connection.saveData) return true;
    if (connection.effectiveType && SLOW_EFFECTIVE_TYPES.includes(connection.effectiveType)) return true;
    return timing.count > 0 && timing.p95 >= SLOW_P95_MS;
};

const getConnection = (): NetworkInformationLike | undefined => {
    if (typeof navigator === 'undefined') return undefined;
    const nav = navigator as Navigator & {
        connection?: NetworkInformationLike;
        mozConnection?: NetworkInformationLike;
        webkitConnection?: NetworkInformationLike;
    };
    return nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
};

const readConnectionInfo = (): UiaConnectionInfo => {
    const conn = getConnection();
    if (!conn) return { supported: false };
    return {
        supported: true,
        effectiveType: conn.effectiveType,
        downlink: conn.downlink,
        rtt: conn.rtt,
        saveData: conn.saveData,
    };
};

/** Pure aggregation of samples into timing metrics. Exported for testing. */
export const computeTimingStats = (samples: readonly UiaSample[]): UiaTimingStats => {
    if (!samples.length) return EMPTY_TIMING;
    const durations = samples.map((s) => s.duration).sort((a, b) => a - b);
    const len = durations.length;
    const sum = durations.reduce((acc, d) => acc + d, 0);
    const mid = Math.floor(len / 2);
    const median = len % 2 ? durations[mid] : (durations[mid - 1] + durations[mid]) / 2;
    const p95 = durations[Math.min(len - 1, Math.floor(0.95 * len))];
    return {
        count: len,
        average: sum / len,
        median,
        min: durations[0],
        max: durations[len - 1],
        p95,
        last: samples[samples.length - 1].duration,
    };
};

const buildSnapshot = (timing: UiaTimingStats, connection: UiaConnectionInfo, inFlight: UiaInFlightInfo): UiaRequestStats => ({
    ...timing,
    connection,
    inFlight,
    isSlowConnection: computeIsSlowConnection(timing, connection),
});

class UiaPerfCollector {
    private buf: UiaSample[] = [];
    private observer?: PerformanceObserver;
    private connection?: NetworkInformationLike;
    private readonly listeners = new Set<() => void>();
    /** Start timestamp of every request currently on the wire, keyed by the id handed to the caller. */
    private readonly inFlight = new Map<number, number>();
    private nextRequestId = 1;
    private slowTimerId?: ReturnType<typeof setTimeout>;
    private snapshot: UiaRequestStats = buildSnapshot(EMPTY_TIMING, readConnectionInfo(), EMPTY_IN_FLIGHT);

    /**
     * Begin observing `/uia/*` resource timings and connection changes.
     * Idempotent — safe to call under React StrictMode / HMR double-invocation.
     */
    start(): void {
        if (typeof window === 'undefined') return;

        if (!this.observer && typeof PerformanceObserver !== 'undefined') {
            this.observer = new PerformanceObserver((list) => {
                let changed = false;
                for (const entry of list.getEntries()) {
                    const { name } = entry;
                    if (!name.includes(UIA_MARKER) || name.includes(WS_MARKER)) continue;
                    this.buf.push({ duration: entry.duration, startTime: entry.startTime, route: name });
                    if (this.buf.length > WINDOW_CAPACITY) this.buf.shift();
                    changed = true;
                }
                if (changed) this.recompute();
            });
            // `buffered: true` replays resource entries that fired before we subscribed.
            this.observer.observe({ type: 'resource', buffered: true });
        }

        if (!this.connection) {
            const conn = getConnection();
            if (conn) {
                this.connection = conn;
                conn.addEventListener('change', this.recompute);
            }
        }

        this.recompute();
    }

    /** Stop observing and release listeners. */
    stop(): void {
        this.observer?.disconnect();
        this.observer = undefined;
        this.connection?.removeEventListener('change', this.recompute);
        this.connection = undefined;
        clearTimeout(this.slowTimerId);
        this.slowTimerId = undefined;
    }

    /**
     * Marks a request as sent. Call it immediately before the actual `fetch` — not when the action is
     * dispatched — and pass the returned id to {@link markRequestEnd} from a `finally`, so an aborted
     * or failed request releases its slot just like a successful one.
     */
    markRequestStart = (): number => {
        const id = this.nextRequestId++;
        this.inFlight.set(id, nowMs());
        this.rearmSlowTimer();
        this.recompute();
        return id;
    };

    /** Marks the request behind `id` as settled. Unknown or already-released ids are ignored. */
    markRequestEnd = (id: number): void => {
        if (!this.inFlight.delete(id)) {
            return;
        }
        this.rearmSlowTimer();
        this.recompute();
    };

    private readInFlight = (): UiaInFlightInfo => {
        if (!this.inFlight.size) {
            return EMPTY_IN_FLIGHT;
        }
        const oldestElapsed = nowMs() - Math.min(...this.inFlight.values());
        return { count: this.inFlight.size, oldestElapsed, isSlow: oldestElapsed >= SLOW_P95_MS };
    };

    /**
     * Nothing external happens while a request just sits there, so schedule the exact moment the
     * oldest one crosses the threshold and recompute then — that is what flips `inFlight.isSlow` and
     * notifies subscribers without anyone polling.
     */
    private rearmSlowTimer = (): void => {
        clearTimeout(this.slowTimerId);
        this.slowTimerId = undefined;
        if (!this.inFlight.size) {
            return;
        }
        const remaining = SLOW_P95_MS - (nowMs() - Math.min(...this.inFlight.values()));
        if (remaining <= 0) {
            // Already past the threshold — the recompute that follows this call reports it.
            return;
        }
        this.slowTimerId = setTimeout(this.recompute, remaining);
    };

    private recompute = (): void => {
        this.snapshot = buildSnapshot(computeTimingStats(this.buf), readConnectionInfo(), this.readInFlight());
        this.listeners.forEach((listener) => listener());
    };

    /** Subscribe to snapshot changes. Returns an unsubscribe function. */
    subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };

    /** Current snapshot. Reference is stable until the next change. */
    getSnapshot = (): UiaRequestStats => this.snapshot;

    /**
     * Clear the rolling window (e.g. on navigation/session reset). In-flight marks are kept on
     * purpose: dropping the window of finished samples must not lose track of live requests.
     */
    reset(): void {
        this.buf = [];
        this.recompute();
    }
}

/** Singleton collector consumed via the `useUiaRequestStats` hook. */
export const uiaPerf = new UiaPerfCollector();
